import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import DynamicAreaDemandTable from '../../components/hospitalOnboarding/DynamicAreaDemandTable';
import DynamicDestinationsTable from '../../components/hospitalOnboarding/DynamicDestinationsTable';
import hospitalOnboardingService from '../../services/hospitalOnboardingService';
import {
  Mic,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Check
} from 'lucide-react';

const SERVICE_GAPS = [
  'Availability',
  'Response time',
  'Equipment readiness',
  'Pricing',
  'Intercity service'
];

export const Step4AreasDestinations = () => {
  const navigate = useNavigate();
  const { formData, updateFields, saveAndContinue, goBack, saveAndExit, isSaving } = useHospitalOnboarding();
  const [patientConditions, setPatientConditions] = useState([]);

  useEffect(() => {
    let mounted = true;
    hospitalOnboardingService.getPatientConditionsLookup()
      .then((data) => {
        if (mounted && Array.isArray(data) && data.length > 0) {
          setPatientConditions(data);
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch patient conditions for Step 4:', err);
      });
    return () => { mounted = false; };
  }, []);

  const handleGapToggle = (gap) => {
    const currentGaps = formData.service_gaps || [];
    const updated = currentGaps.includes(gap)
      ? currentGaps.filter((g) => g !== gap)
      : [...currentGaps, gap];
    updateFields({ service_gaps: updated });
  };

  const handleContinue = (e) => {
    e.preventDefault();
    saveAndContinue();
  };

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 4 OF 5
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Areas & destinations
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 font-normal">
            Share the areas and inter-facility routes you serve. No patient details are needed.
          </p>
          {/* Smart Ambulance Blue accent line */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=hospital&step=4')}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-xs font-medium text-slate-700 shadow-2xs transition-all cursor-pointer self-start sm:self-auto"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleContinue} className="space-y-6">
        {/* Card 1: Area / PIN code demand */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
          <DynamicAreaDemandTable
            rows={formData.area_demands || []}
            onChange={(updatedRows) => updateFields({ area_demands: updatedRows })}
            patientConditions={patientConditions}
          />
        </div>

        {/* Card 2: Destination / long-distance corridors */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
          <DynamicDestinationsTable
            rows={formData.destination_corridors || []}
            onChange={(updatedRows) => updateFields({ destination_corridors: updatedRows })}
          />
        </div>

        {/* Card 3: Current service gaps */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
          <div className="flex items-center gap-3.5 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-800 leading-tight">
                Current service gaps
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 font-normal">
                Key operational pain points where RISA can optimize dispatch availability.
              </p>
            </div>
          </div>

          {/* Service Gaps Checkbox Pills */}
          <div className="mb-6">
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Service Gaps Experienced
            </label>
            <div className="flex flex-wrap gap-2 sm:gap-2.5">
              {SERVICE_GAPS.map((gap) => {
                const isSelected = (formData.service_gaps || []).includes(gap);
                return (
                  <button
                    key={gap}
                    type="button"
                    onClick={() => handleGapToggle(gap)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs transition-all select-none cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium shadow-2xs'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 font-normal'
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </span>
                    <span>{gap}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Details Textarea */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Additional Details & Specific Corridors
            </label>
            <textarea
              rows={3}
              placeholder="Describe any frequent delays, unserved localities, or vehicle type deficits..."
              value={formData.service_gap_details || ''}
              onChange={(e) => updateFields({ service_gap_details: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all resize-y min-h-[72px]"
            />
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="flex items-center justify-between pt-2 pb-12">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
            >
              <Bookmark className="w-4 h-4 text-slate-400" />
              <span>Save & exit</span>
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-xs transition-colors cursor-pointer"
            >
              <span>Save & continue</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step4AreasDestinations;
